import { useTranslation } from "react-i18next";
import type { DeliveryNoteDetailDto } from "@hisense/shared";
import { Panel } from "@/components/ui/Panel";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { formatDate } from "@/domain/datetime";
export function DeliveryDocument({ dn }: { dn: DeliveryNoteDetailDto }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <Panel className="p-4">
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            [t("common.customer"), dn.billToName],
            [t("deliveryNotes.shipTo"), dn.shipTo],
            [t("common.date"), formatDate(dn.date)],
            [t("deliveryWorkspace.receiptDate"), formatDate(dn.deliveryDate)],
            [t("deliveryWorkspace.receiver"), dn.receiverName],
            [t("deliveryNotes.deliveryMode"), dn.deliveryMode],
            [t("deliveryNotes.vehicleNo"), dn.vehicleNo],
            [t("deliveryNotes.driverName"), dn.driverName],
            [t("deliveryNotes.driverPhone"), dn.driverPhone],
            [t("pi.respBy"), dn.respBy],
            [
              t("lot.pProcess"),
              t(
                dn.pProcess
                  ? "invoiceWorkspace.pProcessOn"
                  : "invoiceWorkspace.pProcessOff",
              ),
            ],
          ].map(([label, value]) => (
            <div className="min-w-0" key={label}>
              <dt className="text-2xs uppercase text-ink-faint">{label}</dt>
              <dd className="mt-1 whitespace-pre-line break-words text-sm">
                {value || "—"}
              </dd>
            </div>
          ))}
        </dl>
        {dn.remarks && (
          <p className="mt-4 whitespace-pre-line border-t border-line pt-3 text-sm">
            {dn.remarks}
          </p>
        )}
      </Panel>
      <Panel>
        <TableShell className="border-0">
          <thead>
            <tr>
              <Th pin>
                {t("deliveryNotes.itemNo")} / {t("common.subRef")}
              </Th>
              <Th>{t("common.description")}</Th>
              <Th>{t("deliveryNotes.ctnNo")}</Th>
              <Th>{t("lot.number")}</Th>
              <Th align="right">{t("deliveryWorkspace.planned")}</Th>
              <Th align="right">{t("deliveryWorkspace.received")}</Th>
              <Th>{t("common.unit")}</Th>
              <Th>{t("common.lineRemark")}</Th>
            </tr>
          </thead>
          <tbody>
            {dn.items.map((it) => (
              <Tr key={it.id ?? it.lineNo}>
                <Td pin className="font-mono text-xs">
                  {it.itemNo} / {it.subRef}
                </Td>
                <Td>
                  <p className="max-w-72 whitespace-normal text-xs">
                    {it.description}
                  </p>
                </Td>
                <Td className="font-mono text-xs">{it.ctnNo || "—"}</Td>
                <Td className="font-mono text-xs">{it.lotNumber || "—"}</Td>
                <Td align="right" className="font-mono">
                  {it.orderedQty.toLocaleString()}
                </Td>
                <Td align="right" className="font-mono">
                  {it.deliveredQty.toLocaleString()}
                </Td>
                <Td className="text-xs">{it.unit || "—"}</Td>
                <Td className="text-xs">{it.remarks || "—"}</Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      </Panel>
      <Panel className="p-4">
        <p className="mb-3 text-xs text-ink-faint">
          {t("packingWorkspace.quantityByUnit")}
        </p>
        <div className="flex flex-wrap gap-6">
          {dn.quantities?.map((q) => (
            <div key={q.unit}>
              <p className="font-mono font-medium">{q.unit}</p>
              <p className="text-sm">
                {t("deliveryWorkspace.planned")}:{" "}
                <span className="font-mono">{q.planned}</span>
              </p>
              <p className="text-sm">
                {t("deliveryWorkspace.received")}:{" "}
                <span className="font-mono">{q.received}</span>
              </p>
              {dn.deliveryStatus === "Delivered" && q.received < q.planned && (
                <p className="text-sm text-warn">
                  {t("deliveryWorkspace.shortage")}:{" "}
                  <span className="font-mono">{q.planned - q.received}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
